import JSZip from 'jszip'
import slugify from '../utils/slugify'
import { toggleItemLoading } from '../render/item_loading'

type DownloadAsset = {
    url: string
    name: string
}

async function fetchAsset(url: string): Promise<Blob | null> {
    try {
        const response = await fetch(url)
        if (!response.ok) return null
        return await response.blob()
    } catch {
        return null
    }
}

function getExtension(blob: Blob, fallback = 'bin'): string {
    const mimeMap: Record<string, string> = {
        // images
        'image/jpeg': 'jpg',
        'image/png': 'png',
        'image/webp': 'webp',
        'image/gif': 'gif',
        'image/svg+xml': 'svg',
        'image/tiff': 'tiff',
        'image/bmp': 'bmp',
        'image/avif': 'avif',
        'image/heic': 'heic',
        'image/heif': 'heif',

        // documents
        'application/pdf': 'pdf',
        'application/msword': 'doc',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'docx',
        'application/vnd.ms-excel': 'xls',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'xlsx',
        'application/vnd.ms-powerpoint': 'ppt',
        'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'pptx',
        'application/vnd.oasis.opendocument.text': 'odt',
        'application/vnd.oasis.opendocument.spreadsheet': 'ods',
        'application/vnd.oasis.opendocument.presentation': 'odp',
        'application/rtf': 'rtf',
        'application/epub+zip': 'epub',

        // text
        'text/plain': 'txt',
        'text/csv': 'csv',
        'text/html': 'html',
        'text/markdown': 'md',
        'application/json': 'json',
        'application/xml': 'xml',
        'text/xml': 'xml',

        // video
        'video/mp4': 'mp4',
        'video/quicktime': 'mov',
        'video/x-msvideo': 'avi',
        'video/x-matroska': 'mkv',
        'video/webm': 'webm',
        'video/mpeg': 'mpeg',
        'video/x-ms-wmv': 'wmv',
        'video/3gpp': '3gp',

        // audio
        'audio/mpeg': 'mp3',
        'audio/wav': 'wav',
        'audio/ogg': 'ogg',
        'audio/flac': 'flac',
        'audio/aac': 'aac',
        'audio/x-m4a': 'm4a',
        'audio/mp4': 'm4a',
        'audio/webm': 'weba',
        'audio/x-wav': 'wav',

        // archives
        'application/zip': 'zip',
        'application/x-rar-compressed': 'rar',
        'application/x-7z-compressed': '7z',
        'application/gzip': 'gz',
        'application/x-tar': 'tar',

        // fonts (occasionally in press kits)
        'font/ttf': 'ttf',
        'font/otf': 'otf',
        'font/woff': 'woff',
        'font/woff2': 'woff2',
    }
    return mimeMap[blob.type] ?? fallback
}

async function downloadAsZip(item: Element, assets: DownloadAsset[], zipName = 'download'): Promise<void> {
    const zip = new JSZip()
    const usedNames = new Map<string, number>()

    await Promise.all(
        assets.map(async ({ url, name }) => {
            const blob = await fetchAsset(url)
            if (!blob) {
                console.warn(`[downloadAsZip] Skipping asset (failed to fetch): ${url}`)
                return
            }

            const ext = getExtension(blob)
            const base = slugify(name)
            const key = `${base}.${ext}`

            // handle duplicate filenames
            const count = usedNames.get(key) ?? 0
            usedNames.set(key, count + 1)
            const finalName = count === 0 ? key : `${base}_${count}.${ext}`

            zip.file(finalName, blob)
        })
    )

    const zipBlob = await zip.generateAsync({ type: 'blob' })
    const objectUrl = URL.createObjectURL(zipBlob)

    const anchor = document.createElement('a')
    anchor.href = objectUrl
    anchor.download = `${slugify(zipName)}.zip`
    anchor.style.display = 'none'
    document.body.appendChild(anchor)
    anchor.click()
    document.body.removeChild(anchor)

    setTimeout(() => {
        URL.revokeObjectURL(objectUrl)
        toggleItemLoading(item, false)
    }, 1500)
}

export { downloadAsZip }
export type { DownloadAsset }
